/*
 * Practice id for the current site.
 *
 * Source of truth, in order:
 *   1. window.__CARE_CONFIG__.practiceId — set by an OPTIONAL per-site file
 *      /runtime-config.js that public/index.html loads synchronously before
 *      the bundle (see the loader there).  The build ships NO such file.
 *   2. process.env.REACT_APP_PRACTICE_ID — baked in at build time from .env
 *      (9928).  Absence of the runtime file, or a malformed one, means this.
 *
 * Read once and cached, so every caller in a page load sees the same value.
 * Safe to call at module level (import time): the runtime file is applied by
 * a blocking <script> in <head>, which runs before the deferred bundle.
 *
 * This module must stay free of code dependencies so it is always fully
 * evaluated before any module that imports it.  The only import is a static
 * image (a URL string, no code), for the practice profiles below.
 */

import osaLogo from "../assets/osa/osa_main_logo.png";
import { screenPrivileges } from "./screenPrivileges";

let cachedPracticeId;

function readRuntimePracticeId() {
  try {
    if (typeof window === "undefined") return undefined;
    const cfg = window.__CARE_CONFIG__;
    if (!cfg || typeof cfg !== "object" || Array.isArray(cfg)) return undefined;
    const v = cfg.practiceId;
    if (typeof v === "number" && Number.isInteger(v) && v > 0) return String(v);
    if (typeof v === "string" && /^\d+$/.test(v.trim())) return v.trim();
  } catch (e) {
    // fall through to the build-time default
  }
  return undefined;
}

export function getPracticeId() {
  if (cachedPracticeId === undefined) {
    cachedPracticeId = readRuntimePracticeId() || process.env.REACT_APP_PRACTICE_ID;
  }
  return cachedPracticeId;
}

export default getPracticeId;

/*
 * Per-practice UI profile.  This build serves more than one practice (care =
 * 9928 C-HIM; osa.brainymed.com = 7825 Odessa Sleep Associates), and some of
 * the UI is wired to 9928 only: its locations (Odessa/Frisco/CODE), C-HIM's
 * RingCentral account and voicemail extensions, the high-bp.com website leads,
 * the legacy Dallas (1111) intake and its patient photo store.  Every such
 * piece asks here, so the gating sits in one place.
 *
 * 9928 keeps every feature on and every label as it was; a practice that is
 * not listed gets the neutral profile (one department, no 9928 features).
 *
 * Optional branding (only a practice that has its own; everyone else keeps
 * each page's built-in C-HIM / BrainyCare art and text):
 *   logo          the practice's logo (an imported image URL)
 *   logoShowsName true when the logo already spells out the practice name,
 *                 so the pages drop the name text beside it
 *   address/phone the letterhead and patient contact lines (9928 keeps its
 *                 own C-HIM lines in place, see features.chimContact)
 *   dashboardDefaultTab  the dashboard's opening status tab (dashfilter);
 *                 1 "Active" when absent
 */

/*
 * Patient-facing intake text (the /onboard-* pages, their consents and the
 * intake PDF / staff view of an intake row), per 9928 location; see
 * intakeBrand() below.
 *
 * C-HIM's text is exactly what those pages printed before CODE. CODE (Athena
 * dept 3, codewtx.com; PLAN-DEPT3 D7, owner 2026-10-18) is the SAME legal
 * entity as C-HIM, Odessa Consultants, PLLC: the same privacy notice and the
 * same consent wording, with CODE's own name where the practice is named, and
 * CODE's own address, phone and email (CODE has no fax line).
 *   name         where the consents and pages name the practice
 *   headerLines  the two-line name in the intake page header
 *   noticeName   the subject of the HIPAA notice's first sentence
 *   site         where "I Don't Agree" sends the patient
 */
const CHIM_INTAKE_TEXT = Object.freeze({
  id: "chim",
  shortName: "C-HIM",
  name: "Center for Hypertension",
  headerLines: Object.freeze(["Center for Hypertension", "& Internal Medicine"]),
  noticeName: "Center for Hypertension",
  legalName: "Odessa Consultants, PLLC",
  site: "https://high-bp.com",
});

const CHIM_CONTACT_TEXT = Object.freeze({
  address: "807 N Hancock Ave, Odessa, TX 79761",
  phone: "432-582-2446",
  fax: "432-582-2960",
  email: "contactus@high-bp.com",
  hours: "",
});

export const codeIntake = Object.freeze({
  id: "code",
  shortName: "CODE",
  name: "Center for Obesity, Diabetes, and Endocrinology",
  headerLines: Object.freeze(["Center for Obesity,", "Diabetes, and Endocrinology"]),
  noticeName: "Center for Obesity, Diabetes, and Endocrinology (CODE), operated by Odessa Consultants, PLLC,",
  legalName: "Odessa Consultants, PLLC",
  site: "https://codewtx.com",
});

// CODE's phone is its own RingCentral line (432) 242-5739 (codewtx.com since
// 10-19; the API's DEPARTMENT_PROFILES[9928][3].phone). NOT (830) 423-5763:
// that is C-HIM's appointment-text number (incident 10-19).
export const codeContact = Object.freeze({
  address: "600 N Washington Ave Ste A, Odessa, TX 79761-4436",
  phone: "(432) 242-5739",
  fax: "",
  email: "contactus@codewtx.com",
  hours: "Monday–Friday, 8:00 AM–5:00 PM",
});

const PRACTICE_PROFILES = {
  9928: {
    name: "C-HIM",
    shortName: "CHIM",
    fullName: "Center for Hypertension & Internal Medicine",
    photoUrl: (pid) =>
      `https://cloud.brainymed.com/chim/public/assets/images/profilepictures/${pid}.png`,
    // Athena departments of practice 9928. 3 = CODE (Center for Obesity,
    // Diabetes, and Endocrinology, 600 N Washington Ave Ste A, Odessa; owner
    // 10-18: Dr. Adupa moves there from 2026-11-01, header label "CODE").
    // The header switcher, the dashboard Refresh loop, RPM, Form Settings
    // and every location filter read this list.
    departments: [
      { id: "1", name: "Odessa" },
      { id: "2", name: "Frisco" },
      { id: "3", name: "CODE" },
    ],
    // Locations that only book their own providers' visits (owner 10-18:
    // CODE). A booking that asks for one of these for a provider who is not
    // there on the visit date (no config.department_from move into it by that
    // date, not homed there) books in the default department, 1, instead
    // (schedule/bookingDepartment.js; the API's booking_departmentid follows
    // the same rule). Frisco is not one: dept-1 shared resources (Procedures,
    // providers 1/3/14) legitimately book Frisco visits.
    providerOnlyDepartments: ["3"],
    // Locations kept apart from the others for location-scoped logins (owner
    // 2026-10-27: "CODE staff see only CODE"). A login tagged with one of
    // these is offered only it (header, Reports and Note Review filters, no
    // "All departments"); a location-scoped login of another location is not
    // offered it in the header, so nobody switches into CODE and is stuck
    // there. all_departments logins see every location, as before.
    separateDepartments: ["3"],
    // The departmentid care sends on Athena writes that hard-coded 1 before
    // CODE (Send to Athena from the fax screen). Mirrors the API's
    // DEPARTMENT_PROFILES[9928][n].athena_department: Odessa and Frisco keep
    // 1 exactly as before, CODE sends 3. A department not listed sends 1.
    athenaDepartments: { 1: "1", 2: "1", 3: "3" },
    // Intake text by the intake row's / route's department (intakeBrand()):
    // CODE's own name and contact lines; every other location C-HIM's.
    intakeText: { 3: { intake: codeIntake, contact: codeContact } },
    // One person under two Athena provider ids: the dashboard provider
    // filter keys a visit of the first id as the second (display only, no
    // data is changed). Owner 2026-10-08: Athena provider 16 "Leanora
    // Brissett, NP" (provider_master row 23, inactive) is Leanora, 15.
    providerAliases: { 16: "15" },
  },
  7825: {
    name: "Odessa Sleep Associates",
    shortName: "OSA",
    fullName: "Odessa Sleep Associates",
    // The photo store OSA's own build uses (recovered OSA source, all sites).
    photoUrl: (pid) => `https://hub.brainymed.com/cron/7825/images/${pid}.png`,
    departments: [{ id: "1", name: "Odessa Sleep Associates" }],
    // OSA's main_logo: the "OSA" mark over "Odessa Sleep Associates" (owner-
    // provided, 245x150, transparent background).
    logo: osaLogo,
    logoShowsName: true,
    // OSA's dashboard opens on no status tab (its filterslice: tab 0).
    dashboardDefaultTab: 0,
    // The MedTrack wall board as OSA's own build draws it (features.osaBoard).
    medtrack: {
      // Lists under the room grid, one per visit status: today's visits whose
      // visit_confirmation equals `status` (a 'visit confirmation' option in
      // the practice's select_master; medtrack/fetch returns those rows in
      // `data`). OSA heads its "Ready for Frances" list "RT".
      statusPanels: [{ status: "Ready for Frances", title: "RT" }],
      // The "Pay-in-Cash" list (fetch's payInCash: visits in 'Pay in Cash').
      payInCash: true,
      // Every occupied room in one colour, text on top (OSA: [bg, text]).
      occupiedColors: ["#e17954", "#FFF"],
      // Provider names on the tiles and lists: the first rule whose `match`
      // occurs in the (lower-cased) name gives its `label`; otherwise the name.
      providerShortNames: [
        { match: "madhu", label: "Dr. Madhu" },
        { match: "suresh", label: "Suresh Prasad" },
        { match: "anjaiah", label: "Anjaiah Kodityal" },
        { match: "gurru", label: "Gurru" },
        { match: "patient", label: "Recall" },
      ],
    },
    // Owner-provided. No email or fax line on letterheads (OSA's fax line,
    // used by Communication > Fax, is in FAX_PROFILES below).
    address: "1001 E 3rd St, Odessa, TX 79761",
    phone: "(432) 337-6669",
    // Booking rules for the slot pickers (features.providerSlotRules,
    // schedule/providerSlotRules.js), keyed by providerid. OSA's own build
    // matched these by provider name (utils/providerSlotRules.js); the ids are
    // the 7825 provider_master rows those names match. A provider not listed
    // keeps care's one patient per slot (OSA let an unlisted name book
    // without limit).
    slotRules: {
      // Suresh Prasad (OSA /prasad/)
      1: { maxPerSlot: 3 },
      // Anjaiah Kodityal (/kodityal/)
      2: { maxPerSlot: 2 },
      // Madhusudhan V Pamganamamula, MD (/madhu/): telemedicine only
      3: { maxPerSlot: 2, typeMatch: "tele", typeLabel: "telemedicine" },
      // Manoher L Gurru, MD (/gurru/): Mon-Fri; Mon and Fri telemedicine only
      4: {
        maxPerSlot: 1,
        allowedDays: [1, 2, 3, 4, 5],
        dayOverrides: {
          1: { typeMatch: "tele", typeLabel: "telemedicine" },
          5: { typeMatch: "tele", typeLabel: "telemedicine" },
        },
      },
    },
    // OSA batch 4a. Messaging as internal call notes (features.callNotes,
    // fax/chat/callNotes.js).
    messaging: {
      // Chips under the note box; one click appends the text.
      quickTags: ["Spoke with patient", "Left voicemail", "Appt scheduled", "Follow-up needed"],
      // Message tags starting with this name a provider ("Dr Suresh"): they
      // feed the provider filter instead of the tag chips.
      providerTagPrefix: "dr ",
      // The conversation list re-reads itself this often (seconds).
      pollSeconds: 60,
    },
    // OSA batch 4b. Allowlists for the slimmed chart and cards (see
    // chartTabAllowed() and friends below). A list absent from a profile
    // means "everything", which is how 9928 keeps every tab and action.
    chart: {
      // NavFrame tab ids: Vitals (3) and Forms (7, the Form Wizard panel --
      // features.formWizard). The first entry is the tab a chart opens on.
      tabs: [3, 7],
      // Intake's sub-tabs: Basic Measurements only.
      vitalsTabs: ["basic"],
      // Sideframe pieces kept (googleReview, chat, lov, diagnosis are the
      // gated ones): Level of Visit, as OSA's sideframe kept it (owner). The
      // mic and LiveScribe are never gated: BlinkScribe recording is why 7825
      // is on care.
      sideframe: ["lov"],
    },
    card: {
      // Dashboard card / list-row / Actions-menu actions, by key. "Send
      // Forms" opens the Form Wizard (features.formWizard); "Staff Form"
      // fills an internal staff form for the patient (features.staffForms).
      // "Chart" (owner 10-07, Dr. Madhu: "not just Intake but also Chart")
      // opens the same chart window as 9928's Chart: on the CareIQ tab for a
      // login v2 answers "n3" (chartDefaultTab), else on Vitals as Intake.
      actions: ["Chart", "Notes", "Take Payment", "Scheduler", "Intake", "Send Forms", "Staff Form"],
      // The actions a card shows when the user has pinned none. Intake first
      // (it opens the chart on Vitals, where the BlinkScribe mic is), then
      // Chart. A pinned button shows whatever the user's Home Screen
      // privileges (7825's Chart / Intake / Notes / Actions rows are inactive,
      // so nobody has the Actions menu); only that menu checks them.
      defaultPins: ["Intake", "Chart", "Notes", "Take Payment"],
      // Card blocks kept (orders, quality, rpm are the gated ones).
      blocks: [],
      // The card's Notes box also lists the visit's order notes, each with a
      // delete (v1 DELETE /orders/delete/multiple).
      orderNotes: true,
    },
    // pCard pieces kept: its Appointment/Payment History tabs and the rail's
    // "Send forms" (Form Wizard) and "Staff form" (internal staff forms).
    // iCard and Document tabs, the rail's Google Review / Chat / Sync and the
    // Risk Adjustments picker are left out.
    pcard: ["appointments", "payments", "forms", "staffForms"],
    // Dashboard filter sections kept (by title).
    dashboardFilters: ["Status", "Providers", "Visit Status"],
  },
};

const NEUTRAL_PROFILE = {
  name: "BrainyCare",
  shortName: "BrainyCare",
  fullName: "BrainyCare",
  departments: [{ id: "1", name: "Main" }],
};

export function isPractice9928() {
  return getPracticeId() === "9928";
}

export function practiceProfile() {
  return PRACTICE_PROFILES[getPracticeId()] || NEUTRAL_PROFILE;
}

// Provider id aliases for the dashboard provider filter: { fromId: toId },
// strings. {} for a practice without any (7825 and the rest).
export function practiceProviderAliases() {
  const map = practiceProfile().providerAliases || {};
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [String(k), String(v)]));
}

// Display name ("C-HIM" / "Odessa Sleep Associates") and the short form used
// in the browser tab title ("CHIM - Reports" / "OSA - Reports").
export function practiceName() {
  return practiceProfile().name;
}

export function practiceShortName() {
  return practiceProfile().shortName;
}

// Full name under the logo on the password pages.
export function practiceFullName() {
  return practiceProfile().fullName;
}

// The practice's departments as [{ id: "1", name }] (ids are strings, the
// shape the server stores in the user config).
export function practiceDepartments() {
  return practiceProfile().departments;
}

// A department's display name ("Odessa", "Frisco", "CODE"), or `fallback`
// for an id the practice does not list.
export function practiceDepartmentName(id, fallback) {
  const hit = practiceDepartments().find((d) => String(d.id) === String(id));
  return hit ? hit.name : fallback;
}

// A department id ("1", "2", ...) only when the practice lists it, else null.
export function offeredDepartmentId(id) {
  if (id === undefined || id === null || id === "") return null;
  const key = String(id).trim();
  return practiceDepartments().some((d) => String(d.id) === key) ? key : null;
}

// The default department ("1"): the first one the practice lists.
export function defaultDepartmentId() {
  const first = practiceDepartments()[0];
  return first ? String(first.id) : "1";
}

// A listed department other than the default one (9928: "2" Frisco, "3"
// CODE), else null.
export function nonDefaultDepartmentId(id) {
  const key = offeredDepartmentId(id);
  return key && key !== defaultDepartmentId() ? key : null;
}

// Whether a location books only its own providers' visits (9928: CODE), see
// profile.providerOnlyDepartments.
export function isProviderOnlyDepartment(id) {
  const key = offeredDepartmentId(id);
  return Boolean(key) && (practiceProfile().providerOnlyDepartments || []).map(String).includes(key);
}

// Whether a location is kept apart for location-scoped logins (9928: CODE),
// see profile.separateDepartments.
export function isSeparateDepartment(id) {
  const key = offeredDepartmentId(id);
  return Boolean(key) && (practiceProfile().separateDepartments || []).map(String).includes(key);
}

// The departmentid an Athena write sends for work in department `id`
// (profile.athenaDepartments): 9928 CODE "3", Odessa / Frisco "1" as before;
// "1" for a department the practice does not list or map.
export function athenaDepartmentId(id) {
  const key = offeredDepartmentId(id);
  const map = practiceProfile().athenaDepartments || {};
  return (key && map[key] && String(map[key])) || "1";
}

// Intake text for an intake of `departmentid` (the department its route
// stamps, or an intake row's departmentid): { id, shortName, name,
// headerLines, noticeName, legalName, site, contact: { address, phone, fax,
// email, hours } }. CODE (9928 dept 3) gets codeIntake/codeContact; any other
// department or practice the C-HIM text the intake pages always printed (a
// non-9928 practice keeps its own letterhead lines through
// features.chimContact / practiceContact()).
export function intakeBrand(departmentid) {
  const key = offeredDepartmentId(departmentid);
  const hit = key ? (practiceProfile().intakeText || {})[key] : null;
  if (hit) return { ...hit.intake, contact: hit.contact };
  return { ...CHIM_INTAKE_TEXT, contact: CHIM_CONTACT_TEXT };
}

// More than one location: the working-location switcher, the "All depts"
// search toggles and the department filters only make sense then.
export function hasMultipleDepartments() {
  return practiceDepartments().length > 1;
}

// The practice's own logo, or `fallback` (the page's built-in C-HIM /
// BrainyCare art) for a practice without one -- 9928 included.
export function practiceLogo(fallback) {
  return practiceProfile().logo || fallback;
}

// The practice logo carries the name: pages show it alone, without the name
// text they put beside the default logo.
export function logoShowsName() {
  return Boolean(practiceProfile().logo && practiceProfile().logoShowsName);
}

// The dashboard's opening status tab (redux filter.tab = dashfilter): 1
// "Active" unless the profile says otherwise (7825: 0, no status tab).
export function practiceDashboardDefaultTab() {
  const { dashboardDefaultTab } = practiceProfile();
  return Number.isInteger(dashboardDefaultTab) ? dashboardDefaultTab : 1;
}

// Address and phone for letterheads / patient contact lines ("" when the
// profile has none; 9928's C-HIM lines stay in place behind chimContact).
export function practiceContact() {
  const { address, phone } = practiceProfile();
  return { address: address || "", phone: phone || "" };
}

// Features wired to practice 9928 only (patientPhotos: any practice with a
// photo store of its own), and the OSA (7825) workflow ported from its own
// build (signinSheet, sharedRooms).
export function practiceFeatures() {
  const is9928 = isPractice9928();
  const is7825 = getPracticeId() === "7825";
  return {
    // Odessa/Frisco header switcher (PUT /user/switch-department).
    workingLocations: is9928,
    // Provider filter (dashboard + sign-in sheet) labels: an MD/DO provider
    // reads "Dr. <Last>" from the rows' own Athena names (owner 2026-10-07:
    // "Dr. Malik"), and the sign-in sheet keys rows like the dashboard
    // (provider_master names). Elsewhere names stay as written (OSA's boards
    // say "Suresh Prasad").
    providerFilterLabels: is9928,
    // Communication > Email / Voice mail: C-HIM's mailbox mirror and its
    // hard-coded RingCentral voicemail extensions (hub PHP).
    chimCommunication: is9928,
    // Communication > Fax (list, viewer, Send Fax): each practice's own
    // RingCentral line, set up in FAX_PROFILES below (practiceFaxProfile()).
    fax: is9928 || is7825,
    // high-bp.com leads: Communication > Appt Requests, Onboarding > Web
    // Requests, and the chat's "send enrollment form" link.
    websiteLeads: is9928,
    // "No insurance on file in Athena" on the dashboard card and in the
    // check-in dialogs (owner 10-15; landingscreen/cards/insuranceWarning.js).
    // 9928, where Athena refused check-ins for Insurance. 7825 is an Athena
    // practice too: to warn there, make this `is9928 || is7825`.
    noInsuranceWarning: is9928,
    // Legacy Dallas (practice 1111 / OpenEMR) intake + unmatched mapping.
    dallasMigration: is9928,
    // C-HIM letterhead and contact lines on patient-facing intake pages.
    chimContact: is9928,
    // The patient intake (/patient, /onboard-*): its HIPAA notice, AI-scribe
    // and treatment consents name C-HIM ("Center for Hypertension"); another
    // practice needs its own legal wording first.
    chimIntake: is9928,
    // A photo store keyed by THIS practice's patient ids (9928, 7825).
    patientPhotos: Boolean(practiceProfile().photoUrl),
    // OSA's sign-in sheet (/signtab, from the nav as in OSA): it lists v1
    // POST /signin/get (checkin/future/get without Cancelled rows; v1-only,
    // never routed to v2) and its table scrolls sideways.
    signinSheet: is7825,
    // Several patients may share a room (OSA: family visits, sleep-study
    // rooms): assigning an occupied room only warns -- nobody is unassigned.
    // 9928 keeps one patient per room and moves the occupant out.
    sharedRooms: is7825,
    // Dashboard Refresh = v2 appointments/sync (booked, writes, the shown
    // date) instead of v1 refresh/apt (landingscreen/syncRefreshRequest.js).
    syncRefresh: is7825,
    // Dashboard Refresh as a v2 server job with a progress banner (stage,
    // "day i of n", counts): landingscreen/refreshProgress.js. Falls back to
    // the old v1 refresh/apt call while v2 lacks the routes.
    refreshProgress: is9928,
    // OSA's MedTrack board (/medtrack/<ak>): today's rooms from v2 GET
    // /medtrack/fetch (no per-patient grouping for 7825; MedTrack/medtrackBoard.js),
    // every occupant of a shared room on its tile, and the status lists,
    // colours and provider names of profile.medtrack. 9928 keeps
    // /medtrack/get?departmentid= and its own board.
    osaBoard: is7825,
    // OSA's front-desk payments: coinsurance is a dollar amount and part of
    // the total (copay + self pay + balance + deductible + coinsurance), the
    // deductible is editable whatever the visit status, the dashboard card's
    // amount due adds deductible and coinsurance too, and Update saves via v1
    // PUT /payments/update-patient-payments (v1-only, pinned to v1). With no
    // coverage picker, the card's coverage pill reads the visit's Athena
    // eligibility instead (landingscreen/cards/coveragestatus.js).
    osaPayments: is7825,
    // OSA batch 3. Per-provider booking rules in the slot pickers
    // (profile.slotRules: capacity per slot, bookable weekdays, telemedicine-
    // only providers/days). 9928 keeps one patient per slot.
    providerSlotRules: is7825,
    // OSA batch 4a. Messaging is internal call notes: every send is v1
    // send-message (chat_master + chat_details only), never schat, and no
    // path texts the patient (send-notification, Google Review, BlinkScribe
    // "Text Message"). Plus OSA's chat list: quick tags, provider filter,
    // 60-second poll, no Lock Messages gate / attachments / News Feed / New
    // Patient, unread badge on the side nav (fax/chat/callNotes.js).
    callNotes: is7825,
    // OSA batch 5. OSA's own Messaging page (/messaging, "Messaging" in the
    // side nav with the unread badge): the call-notes conversation list with
    // the provider dropdown at the top (fax/osaMessaging.jsx). Sends stay call
    // notes (callNotes.postCallNote).
    messagingPage: is7825,
    // OSA batch 5. /logs is OSA's Vapi call log (v1 GET /vapi/calls, pinned to
    // v1; the server picks the practice's assistant -- no id in the bundle).
    // 9928 keeps its OTP log on /logs.
    vapiCallLogs: is7825,
    // OSA batch 5. Online Inquiries (/webinquiry, privilege "Website
    // Inquiry"): v1 website-inquiry/get, update-notes, update-status (pinned
    // to v1, as OSA calls them). 9928 keeps its CareIQ website requests.
    webInquiry: is7825,
    // OSA batch 5. The visit note as a PDF to print: OSA's staff print an
    // encounter as the BlinkScribe note PDF (chart Summary > Notes > PDF,
    // GET {bs}/v1/documents/PDF/<note>, new tab, browser print). 7825's chart
    // has no Summary tab, so a "PDF" button sits next to Edit in the note
    // panel beside the chart (patientcharts/frames/notePdf.jsx).
    notePdf: is7825,
    // Emailing a patient from care (BlinkScribe Share > Email): 9928. Off for
    // 7825 pending the owner's decision -- to offer it there too, make this
    // `is9928 || is7825`.
    patientEmail: is9928,
    // Form Wizard (10-01): staff send forms and screenings (EN/ES) by text,
    // email, link or tablet (dashboard card "Send Forms", pCard rail, chart
    // Forms tab, Form Inbox); the patient fills them in at /f/#<token>; the
    // submissions land in the Form Inbox (/form-inbox, side-nav badge) and the
    // chart's Forms tab for review and Send to EMR; admins set it up in
    // /form-settings. Server: v2 /v1/{pid}/formwiz/* (FORMWIZ_PRACTICES); a
    // 404 from it hides the feature quietly.
    formWizard: is9928 || is7825,
    // Internal staff forms (10-02): forms staff fill themselves for a patient
    // (never sent to the patient): chart Forms tab "Fill staff form", card /
    // Actions-menu "Staff Form" (id 11), pCard rail "Staff form", the Form
    // Inbox's Staff forms tab; Print / Save PDF / Send to EMR; staff complete
    // the staff-only sections of received patient forms. Server: v2
    // /v1/{pid}/formwiz/internal/*; a 404/503 hides it quietly.
    staffForms: is9928 || is7825,
    // The chart Forms tab's own questionnaires (C-HIM's instruments: "+ New
    // Questionnaire" and the formHistory table): 9928, unchanged. 7825's
    // Forms tab shows only the Form Wizard panel.
    legacyQuestionnaires: is9928,
    // CareIQ N3 (10-05): the new CareIQ tab and its chart frame, per login.
    // The practice may ask v2 GET /v1/kg/{pid}/careiq/ui-flag (after login,
    // then every 5 minutes; utils/careiqN3Flag.js); any failure means the
    // legacy tab. 9928 only: here "legacy" is the old CareIQ tab. (7825 asks
    // the flag too, for its N3-only CareIQ tab: careiqTabN3Only() below, where
    // "legacy" means no CareIQ tab at all.)
    careiqN3: is9928,
    // Unified staff Inbox (owner 10-20, Step 1): one "Inbox" side-nav entry
    // (total New badge) in place of Communication + Onboard, and /inbox with
    // its queues -- /inbox/requests (website appointment + new-patient
    // requests and onboarding intakes as one list, v2 /v1/{pid}/inbox/*),
    // /inbox/messages, /inbox/faxes, /inbox/voicemail, /inbox/email,
    // /inbox/forms (the existing Messaging / Fax / Voice mail / Email / Form
    // Inbox components). /fax, /onboarding and /messaging redirect into it.
    // Off: the side nav, routes and pages are exactly as before. 7825 (OSA)
    // stays off until the owner switches it on: make this `is9928 || is7825`
    // (src/inbox/inboxQueues.js already maps OSA's own queues).
    unifiedInbox: is9928,
  };
}

// Provider name as the practice's board shows it (profile.medtrack
// providerShortNames); `fallback(name)` for a practice without that list, so
// each caller keeps its own built-in mapping.
export function medtrackProviderName(provider, fallback) {
  const rules = practiceProfile().medtrack?.providerShortNames;
  if (!rules) return fallback(provider);
  if (!provider) return "";
  const name = String(provider).toLowerCase();
  const hit = rules.find((r) => name.includes(r.match));
  return hit ? hit.label : provider;
}

// Patient-intake routes. All of them render 9928's intake (its consents name
// "Center for Hypertension"; /onboard-code names CODE, see intakeBrand());
// /patient1111 also posts to practice 1111.
// Another practice's route table leaves them out (routes/routes.js), so its
// patients can't reach a consent that names the wrong practice.
const CHIM_INTAKE_PATHS = [
  "/patient", "/patient1111", "/patient-enrollment", "/onboard-odessa", "/onboard-frisco", "/onboard-code",
];

// The department a public intake route stamps on its intake row (9928):
// /onboard-code -> 3 (CODE), /onboard-frisco and the legacy Dallas
// /patient-enrollment link -> 2 (Frisco), everything else (/onboard-odessa,
// /patient) -> 1 (Odessa). A department the practice does not list falls back
// to 1, so a one-location practice always stamps 1.
const INTAKE_ROUTE_DEPARTMENTS = [
  ["/onboard-code", "3"],
  ["/onboard-frisco", "2"],
  ["/patient-enrollment", "2"],
];

// The router-relative path of a browser path: the build's basename
// (PUBLIC_URL, e.g. "/branch/hubfix-dept3-code" on a branch preview, the
// <Router basename> in App.js) is stripped, so "/branch/x/onboard-code" reads
// as "/onboard-code". A path outside the basename, or a build served at the
// root (prod, hub.preview), comes back unchanged. PUBLIC_URL may also be a
// full URL; only its path counts.
export function routerPath(pathname, base = process.env.PUBLIC_URL) {
  const path = String(pathname || "");
  let prefix = String(base || "").trim();
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(prefix)) {
    try {
      prefix = new URL(prefix).pathname;
    } catch (e) {
      prefix = "";
    }
  }
  prefix = prefix.replace(/\/+$/, "");
  if (!prefix) return path;
  if (!prefix.startsWith("/")) prefix = `/${prefix}`;
  const lower = path.toLowerCase();
  const lowerPrefix = prefix.toLowerCase();
  if (lower === lowerPrefix) return "/";
  if (lower.startsWith(`${lowerPrefix}/`)) return path.slice(prefix.length);
  return path;
}

// `pathname` may be the router's location.pathname (already relative) or the
// raw window.location.pathname (basename stripped here); both give the same.
export function intakeDepartmentId(pathname) {
  const path = routerPath(pathname).toLowerCase();
  const hit = INTAKE_ROUTE_DEPARTMENTS.find(([route]) => path.startsWith(route));
  return (hit && offeredDepartmentId(hit[1])) || "1";
}

// Pages ported from OSA's own build (batch 5): only where their feature is on.
// A list = any of them (/messaging is also the unified Inbox's redirect).
const FEATURE_PATHS = {
  "/messaging": ["messagingPage", "unifiedInbox"],
  "/webinquiry": "webInquiry",
  "/inbox": "unifiedInbox",
  "/inbox/requests": "unifiedInbox",
  "/inbox/messages": "unifiedInbox",
  "/inbox/faxes": "unifiedInbox",
  "/inbox/voicemail": "unifiedInbox",
  "/inbox/email": "unifiedInbox",
  "/inbox/forms": "unifiedInbox",
};

export function isRouteForPractice(path) {
  const features = practiceFeatures();
  if (FEATURE_PATHS[path]) {
    const need = [].concat(FEATURE_PATHS[path]);
    return need.some((f) => Boolean(features[f]));
  }
  return features.chimIntake || !CHIM_INTAKE_PATHS.includes(path);
}

// Patient photo URL from the practice's own store (9928: cloud.brainymed.com,
// 7825: hub.brainymed.com/cron/7825). A practice without one gets "" (no
// request; an <img> with an empty src fires onError, so the existing
// initials/placeholder fallbacks run) -- never another practice's store, whose
// ids would show a different patient's face.
export function patientPhotoUrl(patientid) {
  const { photoUrl } = practiceProfile();
  return photoUrl ? photoUrl(patientid) : "";
}

// ---- OSA batch 4b: per-practice allowlists (profile.chart / card / pcard /
// dashboardFilters). A list the profile does not carry allows everything, so
// 9928 (no lists) keeps every tab, action and block exactly as before.
const allows = (list, item) => !Array.isArray(list) || list.includes(item);

// A patient-chart NavFrame tab (by id). `careiqN3`: this chart window's CareIQ
// flag (utils/careiqN3Flag.js useCareiqN3Chart, read when the chart opened and
// kept until it closes), true only for "n3". In a practice whose CareIQ tab is
// N3-only (careiqTabN3Only(), 7825) the CareIQ tab (11) is decided by that flag
// alone: "n3" (the rollout file, the practice's "CareIQ (new tab)" privilege, or
// bmadmin's specialty preview) shows it; "legacy", no answer yet or an error
// never does -- 7825 never gets the old CareIQ tab. Everywhere else (9928) the
// flag is ignored and the tab list decides, as before.
export function chartTabAllowed(id, careiqN3) {
  if (id === 11 && careiqTabN3Only()) return careiqN3 === true;
  return allows(practiceProfile().chart?.tabs, id);
}

// The tab a chart opens on: the profile's first tab, else `fallback`. An N3-only
// CareIQ practice (7825) opens an "n3" chart on CareIQ, as 9928's chart does.
export function chartDefaultTab(fallback, careiqN3) {
  if (careiqTabN3Only() && careiqN3 === true) return 11;
  const tabs = practiceProfile().chart?.tabs;
  return Array.isArray(tabs) && tabs.length ? tabs[0] : fallback;
}

// A chart tab request (NavFrame click, "go to vitals" events): itself when
// allowed, else the chart's opening tab.
export function chartTab(id, careiqN3) {
  return chartTabAllowed(id, careiqN3) ? id : chartDefaultTab(id, careiqN3);
}

// One of Intake's sub-tabs ("basic", "vitals", "other", "prev").
export function vitalsTabAllowed(key) {
  return allows(practiceProfile().chart?.vitalsTabs, key);
}

// A gated sideframe piece: "googleReview", "chat", "lov", "diagnosis".
export function sideframeShows(piece) {
  return allows(practiceProfile().chart?.sideframe, piece);
}

// A dashboard card / list-row / Actions-menu action, by its key ("Chart").
export function cardActionShown(key) {
  return allows(practiceProfile().card?.actions, key);
}

// The keys of the actions a card shows when the user has pinned none, or
// null for the card's own default (9928: Chart, Orders, Notes).
export function cardDefaultPins() {
  const pins = practiceProfile().card?.defaultPins;
  return Array.isArray(pins) ? pins : null;
}

// A gated card block: "orders" (the Orders / Tasks list), "quality" (risk
// adjustment banner, border and badge), "rpm" (RPM tag / icon).
export function cardBlockShown(name) {
  return allows(practiceProfile().card?.blocks, name);
}

// The card's order-notes list (with delete): only where the profile asks.
export function cardOrderNotes() {
  return Boolean(practiceProfile().card?.orderNotes);
}

// A gated pCard piece: tabs "appointments", "payments", "icard", "document";
// rail "review", "chat", "sync", "forms", "staffForms"; "riskAdjustment".
export function pcardShows(piece) {
  return allows(practiceProfile().pcard, piece);
}

// A dashboard filter section, by its title ("Order Status").
export function dashboardFilterShown(title) {
  return allows(practiceProfile().dashboardFilters, title);
}

// ---- OSA batch 6 (dashboard) ----
// One appended block of helpers (not new practiceFeatures() keys, whose key
// list the gate tests pin) so the batch-6 branches merge mechanically.

// C-HIM's staff enrolment on the Users page: after Add User the admin is sent
// to the face-capture wizard (/onboard-emp), and every users-table row has the
// resend-welcome and camera / eye (face photos) icons. 9928 only: OSA's own
// build had all three commented out.
export function staffFaceEnrol() {
  return isPractice9928();
}

// The card view's Notes as OSA's build drew it (7825): one fixed-height scroll
// box with the whole appointment note and the visit's order notes as bullets
// ("No notes" when both are empty), in place of the orange "Notes:" line cut
// at 80 characters and the separate Order Notes block.
export function cardNotesBox() {
  return getPracticeId() === "7825";
}

// Also gated through the existing helpers (no new keys): the list view's
// "Payment - Not updated" warning is card block "paymentWarning" (7825's empty
// card.blocks hides it), and the Payments popup's colour radios and "Retrieve
// last visit" follow practiceFeatures().osaPayments.

// OSA batch 6 (boards-nav)
// ---- MedTrack board extras, sign-in sheet, chart Orders tab, side nav /
// header / profile menu, the update pill, kiosk icons and the Rooms page.
// 7825's keys are merged into its profile here, so the whole batch is this
// one appended block; every accessor below reads the profile, and a practice
// without the key (9928, the neutral profile) keeps today's behaviour.
Object.assign(PRACTICE_PROFILES[7825], {
  // The app's name in the "new version" pill (useAutoUpdate): "care" when
  // absent.
  appName: "OSA",
  // Side-nav entries kept, by label (OSA's own nav, plus Communication --
  // 7825's call notes -- the two BlinkScribe pages below, and Form Inbox,
  // the Form Wizard review queue, features.formWizard). Filtered before the
  // privilege check, so a privilege never brings back a hidden one.
  nav: [
    "Home", "Sign-in", "Messaging", "Communication", "Note Review", "Print Notes",
    "User", "Rooms", "Reports", "Scheduler", "Logs", "Online Inquiries", "Form Inbox", "Logout",
  ],
  // Profile-menu entries kept, by label (OSA's menu: View profile, Logout;
  // plus Note instructions and, for practice admins, Practice vocabulary -
  // both for BlinkScribe users, below).
  profileMenu: ["View profile", "Note instructions", "Practice vocabulary", "Logout"],
  // Shown only to a user with BlinkScribe access in this practice (one
  // GET {bs}/auth/me per session, utils/bsAccess.js); without it the mic and
  // the note panel in the chart are hidden too. Owner decision 10-01.
  bsAccessOnly: ["Note Review", "Print Notes", "Note instructions", "Practice vocabulary"],
  // Routes this practice does not have (typed URLs: "Page not available").
  hiddenPaths: ["/forms"],
  // The chart window's orange "Orders" side tab and its order panel.
  chartOrdersPanel: false,
  // The dashboard toolbar's kiosk status icons and their 1-minute poll.
  kioskStatus: false,
  // The Rooms page (/rooms, "Rooms" in the side nav): list every room with
  // its status, add, rename, set Active/Inactive (v1 rooms/fetch,
  // create_room, updt_rm_label). Needs any of `privilegeAny` (Side Bar
  // privileges: the "Rooms" one split off "Users" 11-03, or "Users";
  // `privilege` is the old single name, kept for older readers); delete also
  // needs a user role (grole) in `deleteRoles` -- care's admin roles (IT -
  // Admin, IT - Development, Practice - Admin, IT - QA, as manage-profile uses).
  roomsAdmin: { privilege: "Users", privilegeAny: screenPrivileges("rooms"), deleteRoles: ["6", "7", "8", "9"] },
  // OSA's sign-in sheet (with features.signinSheet): statuses its default
  // list leaves out, no filter icon, no Print/Reprint column (C-HIM's
  // encounter form; OSA prints the BlinkScribe note PDF).
  signin: { excludeStatuses: ["Tele - Confirmed"], filterIcon: false, printColumn: false },
});
Object.assign(PRACTICE_PROFILES[7825].medtrack, {
  // No Tele-Health panel (OSA removed it).
  telePanel: false,
  // Coloured visit-type badges (Walk-In, Labs/Inj, New, Follow Up, Rep) in
  // the waiting list and the status / Pay-in-Cash lists.
  purposeBadges: true,
  // One room grid of this many columns (no grouping by first letter), empty
  // rooms in `emptyColors` with "Available", and an Available / Occupied
  // legend under the grid.
  roomColumns: 5,
  emptyColors: ["#E1EFFF", "#7AA7FF"],
  legend: true,
  // Letters of the surname on tiles and lists ("Last, F"): 3 when absent.
  surnameLetters: 4,
});

// The app's name in user-facing update messages ("care" unless the profile
// names it).
export function practiceAppName() {
  return practiceProfile().appName || "care";
}

// A side-nav entry, by its label.
export function navItemShown(label) {
  return allows(practiceProfile().nav, label);
}

// A profile-menu entry, by its label.
export function profileMenuItemShown(label) {
  return allows(practiceProfile().profileMenu, label);
}

// An entry (nav label / menu label) that needs BlinkScribe access in this
// practice. False everywhere for a practice without the list (9928): nothing
// is checked or hidden there.
export function bsAccessOnly(label) {
  const list = practiceProfile().bsAccessOnly;
  return Array.isArray(list) && list.includes(label);
}

// The practice gates BlinkScribe UI on the user's access (utils/bsAccess.js).
export function bsAccessGated() {
  const list = practiceProfile().bsAccessOnly;
  return Array.isArray(list) && list.length > 0;
}

// The chart window's Orders side tab and panel (landingscreen/modals/pmodal).
export function chartOrdersPanelShown() {
  return practiceProfile().chartOrdersPanel !== false;
}

// The dashboard's kiosk status icons and poll (landingscreen/navigationmenu).
export function kioskStatusShown() {
  return practiceProfile().kioskStatus !== false;
}

// The Rooms page's rules ({ privilege, deleteRoles }), or null: no page.
export function roomsAdmin() {
  return practiceProfile().roomsAdmin || null;
}

// The sign-in sheet's practice rules: { excludeStatuses, filterIcon,
// printColumn }, each defaulting to today's sheet.
export function signinSheetRules() {
  const s = practiceProfile().signin || {};
  return {
    excludeStatuses: Array.isArray(s.excludeStatuses) ? s.excludeStatuses : [],
    filterIcon: s.filterIcon !== false,
    printColumn: s.printColumn !== false,
  };
}

// Batch 6 routes: /rooms only where the Rooms page is on; the profile's
// hiddenPaths nowhere. Every other path is left to isRouteForPractice.
export function boardsNavRouteAllowed(path) {
  if (path === "/rooms") return Boolean(roomsAdmin());
  const hidden = practiceProfile().hiddenPaths;
  return !(Array.isArray(hidden) && hidden.includes(path));
}

// OSA batch 6 (scheduler-login)
// The login page's Support form, per practice. Every ticket is filed (v1
// support/create-ticket -> the practice's ticket_master), but nothing reads
// that table, so a ticket on its own reaches nobody (auth/supportTicket.js).
//   email       a copy of each ticket goes here through the utility mailer
//               (send-email, the one care's Forgot Password and new-user
//               emails already use). The login page has no token, so a care
//               task or inbox item (both need one) cannot be created from it.
//               Absent: no email (the ticket row only).
//   categories  the "Ticket Category" options; the ticket subject becomes
//               "[<value>] <subject>" (OSA's form). Absent: no category.
//   contact     { name, phone } for a "Direct Technical Support" card, only
//               once the owner supplies one (none is set).
const SUPPORT_DESKS = {
  // OSA's own build mailed every login ticket to the BrainyMed admin mailbox.
  7825: {
    email: "admin@brainymed.com",
    categories: [
      { value: "Technical Issue", label: "Technical / System Issue" },
      { value: "Account Access", label: "Account & Login Assistance" },
      { value: "Clinical Data", label: "Clinical / Patient Records" },
      { value: "Billing & Setup", label: "Billing & Practice Setup" },
      { value: "Feature Request", label: "Feature Request / Feedback" },
    ],
  },
};

export function practiceSupportDesk() {
  const desk = SUPPORT_DESKS[getPracticeId()] || {};
  const { contact } = desk;
  return {
    email: desk.email || "",
    categories: Array.isArray(desk.categories) ? desk.categories : [],
    contact: contact && contact.name && contact.phone ? contact : null,
  };
}

// OSA batch 6 (reports) ------------------------------------------------------
// The Reports page. Kept in this one block -- outside practiceFeatures() and
// PRACTICE_PROFILES, whose key sets the gate tests pin -- so parallel batches
// merge mechanically. 9928 (and any practice not listed) keeps every
// behaviour exactly as before. (The kiosk icons, BlinkScribe-access gating
// and login Support tickets this branch also built are the boards-nav block's
// kioskStatusShown() / bsAccessGated() and the scheduler-login block's
// practiceSupportDesk() above -- one implementation each.)
const BATCH6_PROFILES = {
  7825: {
    // Payment methods the Reports toolbar's red card chip adds up. OSA's own
    // build counts 1, 2, 3 (Care Credit) and 6 (card paid at the kiosk through
    // OSA's Stripe account); every OSA payment since 07-20 is a 6, so care's
    // [1, 2] showed $0.00.
    cardChipMethods: [1, 2, 3, 6],
  },
};

export function practiceFeaturesB6() {
  const is7825 = getPracticeId() === "7825";
  return {
    // Reports > "PDF": the one-click A4 Payment Report of OSA's own build
    // (Reports/paymentReportPdf.js; jspdf + jspdf-autotable, loaded lazily).
    paymentReportPdf: is7825,
    // Reports as OSA's build drew it: Daily Totals is GRAND TOTAL alone (no
    // ONLINE / KIOSK, CARD / CASH, SQUARE / STRIPE, CARECREDIT / CHECK boxes),
    // no "Past Appt." button, no delete-request column, flag or delete icon.
    reportsSlim: is7825,
  };
}

// The payment methods (patient_payments.payment_method) in the Reports red
// card chip: the profile's list, else care's own [1, 2].
export function reportsCardChipMethods() {
  const methods = BATCH6_PROFILES[getPracticeId()]?.cardChipMethods;
  return Array.isArray(methods) ? methods : [1, 2];
}

// ---- CareIQ in OSA (10-07) -------------------------------------------------
// Owner 10-07: Dr. Madhu uses CareIQ in OSA (7825). One block, outside
// practiceFeatures() and the profile literals (whose key sets the gate tests
// pin), so parallel branches merge mechanically. 9928 (no `careiq` key) keeps
// every behaviour exactly as before.
//   tab      "n3": the chart's CareIQ tab exists only for a login v2's ui-flag
//            answers "n3" (chartTabAllowed); the practice asks the flag after
//            login like 9928 (utils/careiqN3Flag.js). No legacy CareIQ tab.
//   orders   false: the N3 tab places no orders and confirms no diagnoses --
//            no Add buttons (an order reads as text), no "Show orders without
//            recording", no order zone after recording. OSA's chart has no
//            Orders panel or diagnosis piece (chartOrdersPanel false,
//            sideframe ["lov"]); its orders are placed in athenaNet.
//   labSync  false: the timeline's search / "Show all" re-reads the local lab
//            list only, never the paid Athena lab sync (7825 has no Labs tab).
Object.assign(PRACTICE_PROFILES[7825], {
  careiq: { tab: "n3", orders: false, labSync: false },
});

// The CareIQ tab is N3-only here (7825): see chartTabAllowed().
export function careiqTabN3Only() {
  return practiceProfile().careiq?.tab === "n3";
}

// Pulse, the chart assistant (top-bar button -> PulseChat panel; v2 POST
// /v1/kg/{pid}/pulse/{patientid}, answered from the practice's CareIQ KG).
// 9928: every chart. An N3-only CareIQ practice (7825): the "n3" chart only,
// where its CareIQ tab is (Dr. Madhu works in both practices). Others: no.
export function pulseChatShown(careiqN3) {
  if (isPractice9928()) return true;
  if (careiqTabN3Only()) return careiqN3 === true;
  return false;
}

// The N3 tab may place orders / confirm diagnoses (9928: yes; 7825: no).
export function careiqOrdersShown() {
  return practiceProfile().careiq?.orders !== false;
}

// The N3 timeline's lazy lab sync (a paid Athena call) runs (9928: yes; 7825: no).
export function careiqLabSync() {
  return practiceProfile().careiq?.labSync !== false;
}

// ---- Fax (10-05) -----------------------------------------------------------
// Communication > Fax per practice (features.fax). One block, outside
// PRACTICE_PROFILES, so parallel branches merge mechanically. Not secret: the
// RingCentral credentials are on the server only (v2 reads
// /etc/brainymed/config/ringcentral/<pid>.json + .env; 9928's v1 send uses the
// gateway's own env).
//   lines        the practice's inbound fax lines: { extensionId, label, number }
//   sendFrom     the number recipients see on a fax sent from care (RingCentral
//                sends from the sending user's Fax caller ID; the API can't pick
//                it), shown in the Send Fax modal
//   placeholder  the Send Fax modal's recipient placeholder
//   sendRoute    "v2": POST /v1/{pid}/fax/send on v2 (the practice's own
//                RingCentral account, pinned to v2); "v1": v1's global
//                POST /v1/fax/send (C-HIM's account -- 9928 only; v2 has no
//                9928 send settings yet). Anything else: no sending.
//   sendToEmr    the viewer's "Send to EMR" (care timeline + review task); one
//                filing button per user (fax/faxFilingAccess.js):
//                  true        Send to EMR for every user; filing in Athena is its
//                              "Send to Athena" box (10-07) and there is no
//                              separate Send to Athena button (10-08)
//                  false       no Send to EMR: the Send to Athena button is the
//                              only filing path
//                  "privilege" Send to EMR (with its box) for users with the
//                              Tasks privilege "send to emr", the Send to Athena
//                              button for users with "send to athena"; both
//                              privileges = both buttons (owner 10-16)
//                  unset       as true; any other value as "privilege"
//                Filing in Athena (the box or the button) needs the Tasks
//                privilege "send to athena". Under "privilege", a user with no
//                filing button sees which permissions filing needs.
//   aiLabels     the server reads this practice's new faxes (v2
//                FAX_AI_PRACTICES): the list re-reads itself while a recent
//                fax has no AI read yet (fax/faxAi.js FAX_AI_AWAIT_POLL_MAX)
//   refresh      the viewer's "Check for new faxes": v2 POST /v1/{pid}/fax/
//                refresh (9928's inbound comes from the hub cron instead)
//   showLine     the viewer names the line a fax came in on
const FAX_PROFILES = {
  9928: {
    lines: [
      { extensionId: "348669030", label: "Fax#1", number: "(432) 582-2895" },
      { extensionId: "62906631031", label: "Fax#2", number: "(432) 582-2960" },
    ],
    // The JWT user's own extension (from_ext "~"): the fax lines can't send.
    sendFrom: "(432) 582-2446",
    placeholder: "e.g. (432) 582-2895 or +14325822895",
    sendRoute: "v1",
    // Owner 10-14: staff were confused by care-timeline-only copies vs Athena, so
    // 9928 files the way OSA does - Send to Athena only (page groups included);
    // the filed document reaches the timeline from Athena (fax/athenaFiling.js
    // refreshes the patient's document list right after filing).
    // Owner 10-16: Frisco relied on Send to EMR's care review task, so it is back
    // for users with the Tasks privilege "send to emr" (id 55), as before 10-14;
    // everyone else keeps Send to Athena.
    sendToEmr: "privilege",
    aiLabels: true,
    // Send to Athena opens on this class when the fax has no AI read
    // (staff can always switch Imaging / Clinical document).
    athenaDefaultCategory: "IMAGING",
    refresh: false,
    showLine: false,
  },
  7825: {
    // RingCentral "OSA FAX" (ext 118); label as in the server's 7825.json.
    lines: [{ extensionId: "62960269016", label: "Main fax", number: "(432) 337-6665" }],
    // Owner decision 10-05: outbound faxes come from the fax line's number.
    sendFrom: "(432) 337-6665",
    placeholder: "e.g. (432) 555-0123 or +14325550123",
    sendRoute: "v2",
    // Send to EMR files onto the care timeline + a Tasks review task; 7825's
    // chart has no Documents tab and Tasks is off, so nobody would see it.
    // Send to Athena stays (privilege "send to athena").
    sendToEmr: false,
    aiLabels: true,
    // Owner decision 10-06: with no AI read, Send to Athena opens on Clinical
    // document (goes to a provider for review); staff can switch to Imaging.
    athenaDefaultCategory: "CLINICAL",
    refresh: true,
    showLine: true,
  },
};

// The practice's fax profile, or null (no Communication > Fax).
export function practiceFaxProfile() {
  return FAX_PROFILES[getPracticeId()] || null;
}

// The inbound line a fax arrived on ({ extensionId, label, number }), or null.
export function faxLine(extensionId) {
  const lines = practiceFaxProfile()?.lines || [];
  return lines.find((l) => String(l.extensionId) === String(extensionId)) || null;
}
